/*
 * SYLTBAR — Custom Skio Dynamic Build-a-Box  (single step, auto add-to-cart)
 * -------------------------------------------------------------------------
 * · CASE SIZE (buttons) + DELIVERY (dropdown) + subscription bottles grid.
 * · Floating "Avaline-style" progress pill: "Add N more for [X% off] + Free shipping".
 * · When the box is complete the pill reads "Unlocked Wine Club Membership!" and the box is
 *   AUTOMATICALLY added to cart (bundle w/ selling plan + box props) — then the cart opens.
 * · Each completed box gets its own _dynamicBoxIndex (0,1,2…) so multiple boxes stay
 *   SEPARATE in the cart instead of merging into the first one.
 * · "Reset box after add to cart" (section setting): ON = clear the builder after each add;
 *   OFF = leave it as-is. Either way, a new box is a new box (unique index).
 * · Review stars: Loox. Ratings come from templates/product.loox-rating.liquid
 *   (/products/<handle>?view=loox-rating), one request per box product on load.
 *
 * The endpoint returns { ok, DynamicBox }, so we unwrap data.DynamicBox.
 * Debug: section "Debug logging", ?skio_debug=1, or window.SKIO_BAB_DEBUG=true.
 */
(function () {
  'use strict';

  const CART_ADD_URL = '/cart/add.js';
  const TAG = '[Skio BAB]';

  /* ---------- Skio API (hardcoded for SYLTBAR) ---------- */
  const SKIO_ENDPOINT = 'https://api.skio.com/storefront-http/get-dynamic-box';
  const PRODUCT_PLATFORM_ID = '7472220176439';

  /* ---------- debug ---------- */
  let DEBUG = false;
  try {
    if (window.SKIO_BAB_DEBUG === true) DEBUG = true;
    if (/[?&]skio_debug=1(&|$)/.test(window.location.search)) DEBUG = true;
  } catch (e) {}
  const log = (...a) => { if (DEBUG) console.log(TAG, ...a); };
  const step = (n) => { if (DEBUG) console.log(`%c${TAG} \u25B8 ${n}`, 'color:#a3854a;font-weight:bold'); };
  const warn = (...a) => console.warn(TAG, ...a);
  const fail = (...a) => console.error(TAG, ...a);

  /* ---------- helpers ---------- */
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  const numericId = (id) => String(id == null ? '' : id).split('/').pop();

  function money(value, currency, inCents) {
    let amount = inCents ? Number(value) / 100 : Number(value);
    if (isNaN(amount)) amount = 0;
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount); }
    catch (e) { return `$${amount.toFixed(2)}`; }
  }
  function shortIntervalLabel(interval, count) {
    const unit = { DAY: 'Day', WEEK: 'Week', MONTH: 'Month', YEAR: 'Year' }[String(interval || '').toUpperCase()] || 'Delivery';
    count = Number(count) || 1;
    return `Every ${count} ${unit}${count > 1 ? 's' : ''}`;
  }
  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  function cssEscape(v) {
    if (window.CSS && CSS.escape) return CSS.escape(String(v));
    return String(v).replace(/([^\w-])/g, '\\$1');
  }

  /* ---------- component ---------- */
  function SkioBox(root) {
    this.root = root;
    this.endpoint = SKIO_ENDPOINT;
    this.productPlatformId = PRODUCT_PLATFORM_ID;
    this.priceInCents = root.dataset.priceInCents === 'true';
    this.redirectToCart = root.dataset.redirectToCart === 'true';
    this.resetAfterAdd = root.dataset.resetAfterAdd === 'true';
    this.showReviews = root.dataset.showReviews === 'true';

    this.$controls = root.querySelector('[data-skio-build-controls]');
    this.$tiers = root.querySelector('[data-skio-tiers]');
    this.$freqs = root.querySelector('[data-skio-freqs]');
    this.$grid = root.querySelector('[data-skio-grid]');
    this.$bar = root.querySelector('[data-skio-bar]');
    this.$error = root.querySelector('[data-skio-error]');

    this.state = {
      box: null,
      currency: 'USD',
      tiers: [],
      activeSize: null,
      frequencies: [],
      freqIndex: 0,
      selections: {},   // bundle bottles
      cardVariant: {},
      adding: false,
      autoAdded: false, // guards the auto add-to-cart so it fires once per completion
      committed: false, // true once this box exists in the cart -> further edits sync in place (no re-add)
      boxIndex: 0,      // index of the box being built/edited (hydrated from cart)
      savedByTier: {},  // case size -> the bottles the shopper had in that case.
                        //   Switching 24 -> 12 no longer wipes anything: the 12 you
                        //   built earlier comes straight back, and your 24 is kept
                        //   for when you switch up again.
      cartLines: {},    // variantId(str) -> { key, quantity } for THIS box's lines already in the cart
                        //   presence in this map === "existing line" (update via /cart/change)
                        //   absence === "new item" (create via /cart/add). Kept fresh from every
                        //   mutation response so we never re-fetch /cart.js per edit.
      availability: {}, // { numericVariantId: true|false } stock status from Shopify Product API
      reviews: {}       // { numericProductId: { rating, raters } } Loox data from ?view=loox-rating
    };

    this._seq = 0;      // add-order counter: object keys are numeric variant ids,
                        // which JS sorts numerically, so insertion order is lost
                        // without this. Used when a case has to be trimmed.
  }

  SkioBox.prototype.init = function () {
    if (this.root.dataset.debug === 'true') DEBUG = true;
    step('init');
    this.bindEvents();

    // Reflect cart edits made elsewhere (e.g. in the side cart) back into the builder.
    document.addEventListener('cart:updated', this.onCartUpdated.bind(this));

    this.fetchBox()
      .then((box) => {
        this.state.box = box;
        this.state.currency = box.currencyCode || 'USD';
        try {
          this.computeTiers();
          this.computeFrequencies();
          this.orderProducts();
        } catch (e) { fail('Render failed:', e); this.showError('We couldn’t display the box. See console.'); return; }
        // hydrateFromCart replaces syncBoxIndex: it pre-fills selections from the box
        // already in the cart (if any) so the builder mirrors the cart on load.
        // fetchReviews never rejects (each failure is caught), so it cannot block the grid.
        return Promise.all([this.fetchAvailability(), this.fetchReviews(), this.hydrateFromCart()]).then(() => {
          try {
            this.render();
            step('ready');
          } catch (e) { fail('Render failed:', e); this.showError('We couldn’t display the box. See console.'); }
        });
      })
      .catch((err) => { fail('Fetch failed:', err); this.showError('We couldn’t load the box right now. Please refresh and try again.'); });
  };

  /* ----- re-hydrate when the cart changes elsewhere (side cart edits -> builder) ----- */
  SkioBox.prototype.onCartUpdated = function () {
    if (this._hydrating) return;          // ignore our own writes
    if (!this.state.box) return;
    this._hydrating = true;
    this.hydrateFromCart().then(() => {
      this._hydrating = false;
      try { this.renderTiers(); this.renderFreqs(); this.renderGrid(); this.updateBar(); }
      catch (e) { warn('re-render after cart update failed', e); }
    });
  };

  /* ----- helpers for hydration ----- */
  SkioBox.prototype.findProductVariantByVariantId = function (variantId) {
    const products = this.products();
    for (let i = 0; i < products.length; i++) {
      const p = products[i];
      for (let j = 0; j < p.productVariants.length; j++) {
        const v = p.productVariants[j];
        if (String(numericId(v.platformId)) === String(variantId)) return { product: p, variant: v };
      }
    }
    return null;
  };

  /* Largest configured case size — the only real ceiling. */
  SkioBox.prototype.maxTier = function () {
    const sizes = this.state.tiers.map((t) => t.size).sort((a, b) => a - b);
    return sizes.length ? sizes[sizes.length - 1] : 0;
  };

  /* Only used when LOADING a box that is already in the cart, so the case control
     matches what is there. It is no longer a growth path: the shopper must pick a
     bigger case size before extra bottles are allowed in. */
  SkioBox.prototype.ensureTierFor = function (total) {
    const max = this.maxTier();
    if (max && total > max) return false;
    const fitted = this.pickTierForTotal(total);
    if (fitted != null && fitted !== this.state.activeSize) {
      this.state.activeSize = fitted;
      this.renderTiers();                    // keep the case-size pills in step
      log('case auto-upgraded to', fitted, 'for', total, 'bottles');
    }
    return true;
  };

  /* True when the box holds exactly one of the configured case sizes. */
  /* Complete = the case the shopper SELECTED is full.
     This used to return true whenever the total happened to match any tier, so
     carrying 12 bottles into case 36 counted as "complete" and popped the cart
     open the moment the case was changed. */
  SkioBox.prototype.isCompleteCase = function () {
    const total = this.total();
    return total > 0 && total === this.state.activeSize;
  };

  /* Room left in the case the shopper has SELECTED. The box never grows past it
     on its own — to add more bottles they pick a bigger case size (12 -> 24 -> 36…)
     from the Case size control. */
  SkioBox.prototype.remainingCapacity = function () {
    return this.remainingToCase();
  };

  /* Bottles still needed to complete the case currently selected. */
  SkioBox.prototype.remainingToCase = function () {
    const target = this.state.activeSize || 0;
    return Math.max(0, target - this.total());
  };

  /* Open the cart drawer. NOTE: 'CartDrawer:open' is what HeaderCart *emits*;
     the event it *listens* for is 'cart:open'. Dispatching the wrong one was
     why the cart stopped opening after the first box. */
  /* Publish the SELECTED case size as a cart attribute.
     The cart holds the last COMPLETE case, so from the cart's own data there is
     no way to tell whether the shopper is now filling a bigger one. Without this
     the drawer kept the "+" locked at 12 after they had moved to case 24.
     Cart attributes ride along with any /cart/update.js, so this is free on a
     normal sync and one small request when only the case size changed. */
  SkioBox.prototype.caseAttributes = function () {
    return { _wc_case: String(this.state.activeSize || '') };
  };

  SkioBox.prototype.writeCaseAttribute = function () {
    if (!this.state.committed) return Promise.resolve();
    const size = String(this.state.activeSize || '');
    if (this._writtenCase === size) return Promise.resolve();
    this._writtenCase = size;

    return fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: this.caseAttributes() })
    })
      .then((r) => r.json())
      .then((cart) => {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      })
      .catch((err) => { warn('case attribute write failed:', err); });
  };

  SkioBox.prototype.openCartDrawer = function () {
    document.dispatchEvent(new CustomEvent('cart:open'));
  };

  SkioBox.prototype.pickTierForTotal = function (total) {
    const sizes = this.state.tiers.map((t) => t.size).sort((a, b) => a - b);
    if (!sizes.length) return this.state.activeSize;
    const exact = sizes.filter((s) => s === total)[0];
    if (exact != null) return exact;
    const gte = sizes.filter((s) => s >= total)[0];
    return gte != null ? gte : sizes[sizes.length - 1];
  };

  /* ----- hydrate the builder from the box currently in the cart -----
     - ?edit_box=N (from the cart "Edit" button) targets a specific box index
     - otherwise the most recent box in the cart is loaded
     - if no box exists, we start a fresh build at a unique index          */
  SkioBox.prototype.hydrateFromCart = function (knownCart) {
    const box = this.state.box;
    if (!box || box.boxId == null) return Promise.resolve();

    let wantedIndex = null;
    try {
      const p = new URLSearchParams(window.location.search).get('edit_box');
      if (p != null && p !== '') wantedIndex = String(Number(p));
    } catch (e) {}

    // cart:updated hands us the cart — reuse it instead of asking Shopify again.
    const source = (knownCart && knownCart.items)
      ? Promise.resolve(knownCart)
      : fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((res) => res.json());

    return source
      .then((cart) => {
        const boxLines = (cart.items || []).filter((it) => {
          const pr = it.properties || {};
          return String(pr._dynamicBoxId) === String(box.boxId) && pr._dynamicBoxIndex != null;
        });

        if (!boxLines.length) {
          // nothing in the cart yet -> fresh build
          this.state.selections = {};
          this.state.cardVariant = {};
          this.state.cartLines = {};
          this.state.boxIndex = 0;
          this.state.committed = false;
          this.state.autoAdded = false;
          return;
        }

        // choose which box index to edit
        let targetIndex;
        if (wantedIndex != null && boxLines.some((l) => String(l.properties._dynamicBoxIndex) === wantedIndex)) {
          targetIndex = wantedIndex;
        } else {
          targetIndex = String(boxLines.reduce((max, l) => {
            const idx = Number(l.properties._dynamicBoxIndex);
            return idx > max ? idx : max;
          }, -1));
        }

        const targetLines = boxLines.filter((l) => String(l.properties._dynamicBoxIndex) === targetIndex);

        const selections = {};
        const cardVariant = {};
        const cartLines = {};
        let total = 0;
        let sellingPlanId = null;

        targetLines.forEach((line) => {
          // remember the live cart line so later edits know it's "existing" (has a key)
          cartLines[String(line.variant_id)] = { key: line.key, quantity: line.quantity };
          const match = this.findProductVariantByVariantId(line.variant_id);
          if (!match) { warn('cart variant not in box config:', line.variant_id); return; }
          selections[String(match.variant.platformId)] = {
            product: match.product, variant: match.variant, qty: line.quantity,
            seq: ++this._seq                       // cart line order == add order
          };
          cardVariant[match.product.productPlatformId] = match.variant.platformId;
          total += line.quantity;
          if (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan) {
            sellingPlanId = line.selling_plan_allocation.selling_plan.id;
          }
        });

        this.state.selections = selections;
        this.state.cardVariant = cardVariant;
        this.state.cartLines = cartLines;
        this.state.boxIndex = Number(targetIndex);
        this.state.committed = true;   // this box is live in the cart -> edits sync in place
        this.state.autoAdded = true;   // don't re-add on completion
        // Only choose the case on the FIRST load. Afterwards the shopper owns the
        // Case size control: re-hydrating used to snap 36 back to 24 the moment a
        // bottle was added, because it re-derived the case from the bottle count.
        if (!this._hydratedOnce || total > (this.state.activeSize || 0)) {
          this.state.activeSize = this.pickTierForTotal(total);
        }
        this._hydratedOnce = true;

        if (sellingPlanId != null && this.state.frequencies.length) {
          const fi = this.state.frequencies.findIndex((f) => {
            if (String(f.groupPlanId) === String(sellingPlanId)) return true;
            const map = f.variantMapping || {};
            return Object.keys(map).some((k) => {
              const e = map[k];
              const pid = (e && (e.planId || e.sellingPlanId || e.id)) || e;
              return String(pid) === String(sellingPlanId);
            });
          });
          if (fi > -1) this.state.freqIndex = fi;
        }

        log('hydrated box index', targetIndex, '| bottles', total, '| committed');
      })
      .catch((err) => { warn('hydrateFromCart failed:', err.message); });
  };

  SkioBox.prototype.fetchBox = function () {
    const payload = { productPlatformId: String(this.productPlatformId) };
    step('fetch'); log('POST', this.endpoint, payload);
    return fetch(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then((res) => {
      return res.text().then((text) => {
        let data;
        try { data = JSON.parse(text); }
        catch (e) { fail('Response was not JSON. Raw body:', text); throw new Error(`Skio returned non-JSON (${res.status})`); }
        if (!res.ok) { fail('Skio error body:', data); throw new Error(`Skio responded ${res.status}`); }
        if (data && data.ok === false) { fail('Skio ok:false ->', data); throw new Error('Skio returned ok:false'); }
        const box = (data && data.DynamicBox) ? data.DynamicBox : data;
        if (!box || !box.selectableProductVariants || !box.selectableProductVariants.length) {
          warn('No selectableProductVariants — grid will be empty. Check the box config / response shape.');
        }
        return box;
      });
    });
  };

  /* ----- stock availability (Shopify Product API, by handle) ----- */
  SkioBox.prototype.fetchAvailability = function () {
    const products = this.products();
    const handles = [];
    products.forEach((p) => {
      if (p.productHandle && handles.indexOf(p.productHandle) === -1) handles.push(p.productHandle);
    });
    if (!handles.length) return Promise.resolve();
    step('fetchAvailability'); log('checking stock for handles:', handles);
    const requests = handles.map((handle) =>
      fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
        .then((res) => {
          if (!res.ok) throw new Error(`Product API ${res.status} for ${handle}`);
          return res.json();
        })
        .then((data) => {
          (data.variants || []).forEach((v) => {
            this.state.availability[String(v.id)] = !!v.available;
          });
        })
        .catch((err) => {
          // fail-open: if the stock check itself fails, don't block the whole box over it
          warn('Stock check failed for', handle, '-', err.message, '- treating its variants as available.');
        })
    );
    return Promise.all(requests);
  };

  SkioBox.prototype.isAvailable = function (variant) {
    const val = this.state.availability[numericId(variant.platformId)];
    return val === false ? false : true; // unknown/unchecked -> treat as available
  };

  /* ----- Loox ratings (templates/product.loox-rating.liquid, by handle) -----
     Card markup is built in JS, so Liquid's product.metafields.loox.* is not
     available there. The alternate template prints { id, rating, raters } as
     JSON; we key it by numeric product id. Every failure is caught, so a
     missing template or a bad product never blocks the grid — that card just
     renders the Loox div with empty rating data. */
  SkioBox.prototype.fetchReviews = function () {
    if (!this.showReviews) return Promise.resolve();
    const handles = [];
    this.products().forEach((p) => {
      if (p.productHandle && handles.indexOf(p.productHandle) === -1) handles.push(p.productHandle);
    });
    if (!handles.length) return Promise.resolve();
    step('fetchReviews'); log('loading Loox ratings for handles:', handles);
    const requests = handles.map((handle) =>
      // NOTE: Accept must NOT be application/json — with that header Shopify ignores
      // ?view= and returns the standard product JSON ({ product: {...} }) instead.
      fetch(`/products/${encodeURIComponent(handle)}?view=loox-rating`, { headers: { Accept: 'text/html' } })
        .then((res) => {
          if (!res.ok) throw new Error(`loox-rating view ${res.status} for ${handle}`);
          return res.text();
        })
        .then((text) => {
          // take only the {...} part, so stray whitespace / theme wrappers can't break parsing
          const start = text.indexOf('{');
          const end = text.lastIndexOf('}');
          let data = null;
          if (start > -1 && end > start) {
            try { data = JSON.parse(text.slice(start, end + 1)); } catch (e) { data = null; }
          }
          if (!data || data.id == null || data.product) {
            throw new Error('template not returning rating JSON — got: ' + text.slice(0, 80));
          }
          this.state.reviews[String(data.id)] = {
            rating: parseFloat(data.rating) || 0,
            raters: parseInt(data.raters, 10) || 0
          };
          log('Loox rating', handle, this.state.reviews[String(data.id)]);
        })
        .catch((err) => {
          warn('Loox rating fetch failed for', handle, '-', err.message);
        })
    );
    return Promise.all(requests);
  };


  /* ----- tiers ----- */
  SkioBox.prototype.computeTiers = function () {
    const box = this.state.box;
    const tier = box.percentDiscountTier || box.fixedDiscountTier || {};
    const kind = box.percentDiscountTier ? 'percent' : 'fixed';
    let sizes = Object.keys(tier).map(Number).filter((n) => !isNaN(n));
    if (!sizes.length && Array.isArray(box.sizeInterval) && box.sizeInterval.length) sizes = box.sizeInterval.slice();
    sizes.sort((a, b) => a - b);
    this.state.tiers = sizes.map((size) => ({ size, kind, value: tier[size] }));
    this.state.activeSize = sizes.length ? sizes[0] : null;
    // Shared with the cart so it can validate case sizes and price the tier discount.
    try { window.SKIO_BAB_TIERS = this.state.tiers; } catch (e) {}
    step('tiers'); log('sizes:', sizes, '| activeSize:', this.state.activeSize);
  };

  /* ----- frequencies ----- */
  SkioBox.prototype.computeFrequencies = function () {
    const groups = this.state.box.sellingPlanGroup || {};
    let freqs = Object.keys(groups).map((groupPlanId) => {
      const g = groups[groupPlanId] || {};
      return {
        groupPlanId, interval: g.interval, intervalCount: g.intervalCount,
        variantMapping: g.variantMapping || {}, label: shortIntervalLabel(g.interval, g.intervalCount)
      };
    });
    if (!freqs.length) warn('sellingPlanGroup empty — items would add one-time.');
    const weight = { DAY: 1, WEEK: 7, MONTH: 30, YEAR: 365 };
    freqs.sort((a, b) =>
      (weight[String(a.interval).toUpperCase()] || 30) * (a.intervalCount || 1) -
      (weight[String(b.interval).toUpperCase()] || 30) * (b.intervalCount || 1));
    // if (this.frequencyWeeks) {
    //   const wanted = this.frequencyWeeks.split(',').map((x) => parseInt(x, 10)).filter((n) => !isNaN(n));
    //   if (wanted.length) {
    //     const filtered = [];
    //     wanted.forEach((count) => {
    //       const m = freqs.filter((f) => String(f.interval).toUpperCase() === 'WEEK' && Number(f.intervalCount) === count)[0];
    //       if (m) filtered.push(m); else warn(`No WEEK plan for "${count} weeks".`);
    //     });
    //     if (filtered.length) freqs = filtered;
    //   }
    // }
    this.state.frequencies = freqs;
    // Shared with the cart drawer dropdown so it never has to call Skio itself.
    try { window.SKIO_BAB_FREQUENCIES = freqs; } catch (e) {}
    // if (this.defaultInterval) {
    //   const idx = freqs.findIndex((f) =>
    //     String(f.interval).toUpperCase() === this.defaultInterval &&
    //     (!this.defaultIntervalCount || Number(f.intervalCount) === this.defaultIntervalCount));
    //   if (idx > -1) this.state.freqIndex = idx;
    // }
    log('frequencies:', freqs.map((f) => f.label), '| selected:', this.state.freqIndex);
  };

  /* ----- product order ----- */
  SkioBox.prototype.orderProducts = function () {
    const order = (this.state.box.settings && this.state.box.settings.productVariantOrder) || [];
    const products = this.state.box.selectableProductVariants || [];
    if (!order.length || !products.length) return;
    const index = {};
    order.forEach((id, i) => { index[String(id)] = i; });
    products.sort((a, b) => {
      const av = (a.productVariants[0] && numericId(a.productVariants[0].platformId)) || '';
      const bv = (b.productVariants[0] && numericId(b.productVariants[0].platformId)) || '';
      return (index[av] != null ? index[av] : 9999) - (index[bv] != null ? index[bv] : 9999);
    });
  };

  /* ----- selling plan resolver ----- */
  SkioBox.prototype.resolveSellingPlan = function (freq, variantPlatformId) {
    if (!freq) return null;
    const map = freq.variantMapping || {};
    const vid = String(variantPlatformId);
    const entry = map[vid] != null ? map[vid] : map[numericId(vid)];
    let planId;
    if (entry == null) {
      planId = freq.groupPlanId || null;
      if (Object.keys(map).length) warn('variantMapping missing variant', vid, '-> fallback', planId);
    } else if (typeof entry === 'string' || typeof entry === 'number') {
      planId = entry;
    } else {
      planId = entry.planId || entry.sellingPlanId || entry.id || freq.groupPlanId || null;
    }
    return planId;
  };

  /* ---------- selection state ---------- */
  SkioBox.prototype.total = function () {
    let t = 0;
    for (const k in this.state.selections) if (this.state.selections.hasOwnProperty(k)) t += this.state.selections[k].qty;
    return t;
  };
  SkioBox.prototype.products = function () { return this.state.box.selectableProductVariants || []; };
  SkioBox.prototype.activeVariant = function (product) {
    const vid = this.state.cardVariant[product.productPlatformId];
    return product.productVariants.filter((v) => String(v.platformId) === String(vid))[0] || product.productVariants[0];
  };
  SkioBox.prototype.qtyOf = function (variantId) {
    const s = this.state.selections[String(variantId)];
    return s ? s.qty : 0;
  };
  SkioBox.prototype.changeQty = function (product, delta) {
    const variant = this.activeVariant(product);
    const key = String(variant.platformId);
    const s = this.state.selections;
    const current = s[key] ? s[key].qty : 0;
    if (delta > 0 && !this.isAvailable(variant)) { this.flashLimit(); return; }
    // Hard stop at the selected case size: the shopper must choose a bigger case
    // before more bottles can go in.
    if (delta > 0 && this.total() >= this.state.activeSize) { this.flashLimit(); return; }
    const next = Math.max(0, current + delta);
    if (next === 0) delete s[key];
    else s[key] = { product, variant, qty: next, seq: (s[key] && s[key].seq) || ++this._seq };
    log('changeQty:', product.productTitle, '| qty', next, '| total', this.total(), '/', this.state.activeSize);
    // Box already in cart -> push this line change straight to the cart (builder -> cart sync)
    // Nothing is written to the cart while a case is being filled. The cart keeps
    // the last COMPLETE case; work in progress is held here and pushed in one
    // batch the moment the case is finished (see commitIfComplete).
    if (this.state.committed) this._pendingCart = true;
    this.updateCard(product.productPlatformId);
    this.updateBar();
  };
  SkioBox.prototype.setCardVariant = function (productId, variantId) {
    this.state.cardVariant[productId] = variantId;
    this.updateCard(productId);
  };
  SkioBox.prototype.cloneSelections = function (sel) {
    const src = sel || this.state.selections;
    const out = {};
    Object.keys(src).forEach((k) => {
      out[k] = { product: src[k].product, variant: src[k].variant, qty: src[k].qty, seq: src[k].seq };
    });
    return out;
  };

  SkioBox.prototype.totalOf = function (sel) {
    let t = 0;
    Object.keys(sel).forEach((k) => { t += sel[k].qty; });
    return t;
  };

  /* Remember the current bottles under the case they belong to. */
  /* Every time the bottle count lands exactly on a case size, file that set under
     that case. Filling toward 36 therefore records a usable 12 and 24 on the way,
     so dropping back to them later restores real bottles instead of nothing. */
  SkioBox.prototype.snapshotAtThreshold = function () {
    const total = this.total();
    if (!total) return;
    if (!this.state.tiers.some((t) => t.size === total)) return;
    this.state.savedByTier[total] = this.cloneSelections();
    log('case', total, 'snapshotted while filling');
  };

  /* Keep the first `limit` bottles (in the order they were added) and drop the
     rest. Fallback for a case we have never recorded. */
  SkioBox.prototype.trimSelectionsTo = function (limit) {
    const sel = this.state.selections;
    const keys = Object.keys(sel).sort((a, b) => (sel[a].seq || 0) - (sel[b].seq || 0));
    const out = {};
    let left = limit;
    keys.forEach((k) => {
      if (left <= 0) return;
      const take = Math.min(sel[k].qty, left);
      out[k] = { product: sel[k].product, variant: sel[k].variant, qty: take, seq: sel[k].seq };
      left -= take;
    });
    this.state.selections = out;
  };

  SkioBox.prototype.rememberCase = function () {
    if (this.state.activeSize == null) return;
    this.state.savedByTier[this.state.activeSize] = this.cloneSelections();
  };

  /* Switching case size no longer throws work away.
       - what is on screen is filed under the case you are leaving
       - if you built that case before, it comes back exactly as it was
       - otherwise your bottles carry over when they still fit (12 -> 24)
       - only when they cannot fit (20 bottles -> case 12) do you start empty
     Each case keeps its own set, so 48 -> 12 -> 48 never breaks. */
  SkioBox.prototype.setTier = function (size) {
    const prev = this.state.activeSize;
    if (size === prev) return;

    this.rememberCase();                       // file the case we are leaving

    // Going DOWN is a decision to drop the bigger box, so every case above the
    // one now chosen is forgotten. Without this, 48 -> 24 -> 36 would silently
    // resurrect the abandoned 36, and 36 -> 12 -> 24 would resurrect the 24.
    // After stepping down you build back up from what you are holding.
    if (prev != null && size < prev) {
      Object.keys(this.state.savedByTier).forEach((k) => {
        if (Number(k) > size) delete this.state.savedByTier[k];
      });
      log('stepped down to', size, '- cases above it discarded');
    }

    const saved = this.state.savedByTier[size];
    if (saved && Object.keys(saved).length) {
      this.state.selections = this.cloneSelections(saved);
      log('case', prev, '->', size, ': restored', this.totalOf(this.state.selections), 'bottles');
    } else if (this.total() > size) {
      // Never recorded this case: keep the first `size` bottles that were added
      // and drop the overflow, rather than emptying the box.
      this.trimSelectionsTo(size);
      log('case', prev, '->', size, ': trimmed to the first', size, 'bottles added');
    } else {
      log('case', prev, '->', size, ': carrying', this.total(), 'bottles over');
    }

    this.state.activeSize = size;
    this.state.autoAdded = this.state.committed;   // let an incomplete box re-arm
    this.renderTiers();
    this.renderGrid();

    // Mark the cart dirty BEFORE updateBar(). updateBar() is what calls
    // commitIfComplete(), so setting the flag after it meant the commit saw a
    // clean state and skipped the write — switching 36 -> 12 rebuilt the builder
    // but left 36 sitting in the cart.
    if (this.state.committed) {
      this._pendingCart = true;
      this.writeCaseAttribute();     // unlock the "+" in the cart for the new case
    }

    this.updateBar();
  };
  SkioBox.prototype.setFreq = function (i) {
    this.state.freqIndex = i;
    this.renderFreqs();
    // Box already in cart -> re-map the selling plan on every box line (builder -> cart sync)
    if (this.state.committed) this.syncFrequencyToCart();
  };

  SkioBox.prototype.activeTierPercent = function () {
    const t = this.state.tiers.filter((x) => x.size === this.state.activeSize)[0];
    if (!t || t.kind !== 'percent' || t.value == null) return null;
    let v = Number(t.value);
    if (isNaN(v)) return null;
    if (v > 0 && v < 1) v = v * 100;
    return Math.round(v);
  };

  /* ---------- rendering ---------- */
  SkioBox.prototype.render = function () {
    this.renderTiers();
    this.renderFreqs();
    this.renderGrid();
    this.renderBar();
  };

  SkioBox.prototype.renderTiers = function () {
    const pills = this.state.tiers.map((t) => {
      const active = t.size === this.state.activeSize ? ' is-active' : '';
      return `<button type="button" class="skio-pill${active}" data-skio-tier="${t.size}"><strong>${t.size}</strong> Items</button>`;
    }).join('');
    const options = this.state.tiers.map((t) => {
      const sel = t.size === this.state.activeSize ? ' selected' : '';
      return `<option value="${t.size}"${sel}>${t.size} Items</option>`;
    }).join('');
    this.$tiers.innerHTML =
      `<div class="skio-pills" data-skio-tier-pills>${pills}</div>
      <div class="skio-select skio-select--tier"><select class="skio-select__field" data-skio-tier-select aria-label="Case size">${options}</select></div>`;
  };

  SkioBox.prototype.renderFreqs = function () {
    if (!this.$freqs) return;
    if (!this.state.frequencies.length) { this.$freqs.innerHTML = ''; return; }
    const options = this.state.frequencies.map((f, i) => {
      const sel = i === this.state.freqIndex ? ' selected' : '';
      return `<option value="${i}"${sel}>${esc(f.label)}</option>`;
    }).join('');
    this.$freqs.innerHTML = `<div class="skio-select"><select class="skio-select__field" data-skio-freq-select aria-label="Delivery options">${options}</select></div>`;
  };

  SkioBox.prototype.renderGrid = function () {
    const products = this.products();
    this.$grid.classList.remove('is-loading');
    this.$grid.innerHTML = products.map((p) => this.cardHtml(p)).join('');
    log('rendered', products.length, 'products');
    this.refreshReviews();
  };

  /* 5 stars with a fractional overlay (4.6 -> 92% filled) + review count.
     Products with no reviews render nothing, same as the Loox widget. */
  SkioBox.prototype.reviewStarsHtml = function (product) {
    const rv = this.state.reviews[numericId(product.productPlatformId)];
    if (!rv || !rv.raters) return '';
    const rating = Math.max(0, Math.min(5, rv.rating));
    const pct = (rating / 5) * 100;
    const star = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l2.94 5.96 6.56.95-4.75 4.63 1.12 6.54L12 17.49l-5.87 3.09 1.12-6.54L2.5 9.41l6.56-.95z"/></svg>';
    const five = star + star + star + star + star;
    return `<div class="loox-rating" data-id="${numericId(product.productPlatformId)}" data-rating="${rv.rating}" data-raters="${rv.raters}"></div>`;
    // return `<div class="skio-stars" role="img" aria-label="${rating.toFixed(1)} out of 5 stars, ${rv.raters} reviews">
    //     <span class="skio-stars__track">
    //       <span class="skio-stars__base">${five}</span>
    //       <span class="skio-stars__fill" style="width:${pct}%">${five}</span>
    //     </span>
    //     <span class="skio-stars__count">(${rv.raters})</span>
    //   </div>`;
  };

  SkioBox.prototype.cardHtml = function (product) {
    const variant = this.activeVariant(product);
    const img = variant.imageSrc || product.productImageSrc || '';
    const multi = product.productVariants.length > 1;

    let priceHtml = money(variant.price, this.state.currency, this.priceInCents);
    if (variant.compareAtPrice && Number(variant.compareAtPrice) > Number(variant.price)) {
      priceHtml = `<span class="skio-card__compare">${money(variant.compareAtPrice, this.state.currency, this.priceInCents)}</span> ${priceHtml}`;
    }

    // Loox rating — drawn here from the Loox metafields (fetchReviews), because the
    // Loox script only scans the page once on load and this grid is injected later.
    const reviews = this.showReviews
      ? `<div class="skio-card__reviews">${this.reviewStarsHtml(product)}</div>`
      : '';

    let variantSelect = '';
    if (multi) {
      const opts = product.productVariants.map((v) => {
        const sel = String(v.platformId) === String(variant.platformId) ? ' selected' : '';
        const soldOutTxt = this.isAvailable(v) ? '' : ' (Soldout)';
        return `<option value="${esc(v.platformId)}"${sel}>${esc(v.productVariantTitle)}${soldOutTxt}</option>`;
      }).join('');
      variantSelect = `<div class="skio-select skio-select--variant"><select class="skio-select__field" data-skio-variant data-product-id="${esc(product.productPlatformId)}" aria-label="Choose option">${opts}</select></div>`;
    }

    const blurb = product.productDescription || product.description || '';
    const desc = blurb ? `<div class="skio-card__desc">${esc(blurb)}</div>` : '';

    return `<div class="skio-card" data-skio-card data-product-id="${esc(product.productPlatformId)}">
      <div class="skio-card__img">${img ? `<img src="${esc(img)}" alt="${esc(product.productTitle)}" loading="lazy">` : ''}</div>
      <div class="skio-card__head">
        <span class="skio-card__title">${esc(product.productTitle)}</span>
        <span class="skio-card__price">${priceHtml}</span>
      </div>
      ${reviews}
      ${desc}
      ${variantSelect}
      <div class="skio-card__foot">
        <button type="button" class="skio-card__details" data-skio-details data-handle="${esc(product.productHandle)}">Product Details</button>
        <div class="skio-card__action" data-skio-action>${this.actionHtml(product)}</div>
      </div>
    </div>`;
  };

  SkioBox.prototype.actionHtml = function (product) {
    const variant = this.activeVariant(product);
    const qty = this.qtyOf(variant.platformId);
    if (!this.isAvailable(variant)) {
      return `<span class="skio-btn skio-btn--soldout" style="opacity:.5;cursor:not-allowed;" aria-disabled="true">Soldout</span>`;
    }
    if (qty > 0) {
      return `<div class="skio-stepper"><button type="button" class="skio-stepper__btn" data-skio-dec aria-label="Remove one">&minus;</button><span class="skio-stepper__count">${qty}</span><button type="button" class="skio-stepper__btn" data-skio-inc aria-label="Add one">+</button></div>`;
    }
    return `<button type="button" class="skio-btn skio-btn--add" data-skio-add>Add</button>`;
  };

  SkioBox.prototype.updateCard = function (productId) {
    const card = this.$grid.querySelector(`[data-skio-card][data-product-id="${cssEscape(productId)}"]`);
    if (!card) return;
    const product = this.products().filter((p) => String(p.productPlatformId) === String(productId))[0];
    if (!product) return;
    const variant = this.activeVariant(product);
    const priceEl = card.querySelector('.skio-card__price');
    if (priceEl) {
      let priceHtml = money(variant.price, this.state.currency, this.priceInCents);
      if (variant.compareAtPrice && Number(variant.compareAtPrice) > Number(variant.price)) {
        priceHtml = `<span class="skio-card__compare">${money(variant.compareAtPrice, this.state.currency, this.priceInCents)}</span> ${priceHtml}`;
      }
      priceEl.innerHTML = priceHtml;
    }
    const action = card.querySelector('[data-skio-action]');
    if (action) action.innerHTML = this.actionHtml(product);
    this.syncDetailsAction(productId);
  };

  /* Loox re-render hook. Called after every grid render and when the Product
     Details drawer opens. Loox scans .loox-rating on page load, but this grid is
     injected later (after the Skio fetch), so if the stars don't draw, Loox's
     re-render call goes here. Intentionally a safe no-op until confirmed. */
  SkioBox.prototype.refreshReviews = function () {
    if (!this.showReviews) return;
  };

  /* ---------- progress pill ---------- */
  SkioBox.prototype.tooltipCopy = function () {
    const pct = this.activeTierPercent();
    const kit = window.welcomeKit ? ' + Free Welcome Kit' : '';
    return `We require orders of at least 12 bottles in order to join our SYLTBAR Wine Club and enjoy ${pct}% off + free shipping${kit}, with flexible frequencies every 2, 4, 6 or 8 weeks.`;
  };

  SkioBox.prototype.infoIconHtml = function () {
    return `<button type="button" class="skio-progress__info" data-skio-info aria-label="Why 12 bottles?">
      <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.3"></circle>
        <circle cx="8" cy="4.7" r="0.9" fill="currentColor"></circle>
        <rect x="7.25" y="7" width="1.5" height="5" rx="0.6" fill="currentColor"></rect>
      </svg>
    </button>
    <div class="skio-progress__tooltip" data-skio-tooltip>${esc(this.tooltipCopy())}</div>
    `;
  };

  SkioBox.prototype.renderBar = function () {
    this.$bar.hidden = false;
    this.$bar.innerHTML =
      `<div class="skio-progress" data-skio-progress>
        <div class="skio-progress__msg" data-skio-msg></div>        
        <div class="skio-progress__track">
          <div class="skio-progress__fill" data-skio-fill></div>
        </div>
      </div>`;
    this.updateBar();
  };

  SkioBox.prototype.updateBar = function () {
    const total = this.total();
    this.snapshotAtThreshold();
    const target = this.state.activeSize || 0;
    const remaining = this.remainingToCase();
    const pct = this.activeTierPercent();
    const offTxt = pct != null ? `${pct}% off` : 'Wine Club';
    const msg = this.$bar.querySelector('[data-skio-msg]');
    const fill = this.$bar.querySelector('[data-skio-fill]');
    const progress = this.$bar.querySelector('[data-skio-progress]');
    if (fill) fill.style.width = `${target ? Math.min(total / target, 1) * 100 : 0}%`;

    if (target > 0 && total >= target) {
      if (progress) progress.classList.add('skio-progress--done');
      log("progress:",progress,this.state.adding)
      if (msg) msg.innerHTML = 'Unlocked Wine Club Membership!' + this.infoIconHtml();
      this.rememberCase();          // a finished case is kept for this session
      this.commitIfComplete();
    } else {
      if (progress) progress.classList.remove('skio-progress--done');
      const kit = window.welcomeKit ? ' + Free Welcome Kit' : '';
      if (msg) msg.innerHTML = `Add <b> ${remaining} </b> more for <span class="skio-progress__badge">${esc(offTxt)}</span> + Free shipping${kit}` + this.infoIconHtml();
      this.state.autoAdded = false;

      // Emptying the builder must empty the cart too, otherwise the shopper walks
      // away with a box they thought they had removed. This is the only write that
      // happens while a case is incomplete.
      if (this.state.committed && total === 0 && this._pendingCart && !this._syncing) {
        this._pendingCart = false;
        this.syncSelectionsToCart();
      }
    }
  };

  /* The one place the cart is written from the builder.
     Fires only when the selected case is exactly full:
       - first ever box   -> /cart/add.js with every bottle
       - existing box     -> one /cart/update.js for the lines already there,
                             plus one /cart/add.js for bottles that are new
     Everything the shopper did in between stayed local, so the cart never
     flickers, never re-hydrates the grid, and only opens once. */
  SkioBox.prototype.commitIfComplete = function () {
    if (!this.isCompleteCase()) return;
    if (this.state.adding || this._syncing) return;

    if (!this.state.committed) {
      if (this.state.autoAdded) return;
      this.state.autoAdded = true;
      this.addToCart();
      return;
    }

    if (!this._pendingCart) return;          // nothing changed since the last commit
    this._pendingCart = false;
    this._openCartAfterSync = true;
    this.syncSelectionsToCart();
  };

  SkioBox.prototype.flashLimit = function () {
    const p = this.$bar.querySelector('[data-skio-progress]');
    if (!p) return;
    p.classList.add('skio-shake');
    setTimeout(() => p.classList.remove('skio-shake'), 450);
  };

  SkioBox.prototype.showError = function (msg) {
    if (this.$grid) this.$grid.innerHTML = '';
    if (this.$controls) this.$controls.hidden = true;
    if (this.$bar) this.$bar.hidden = true;
    if (this.$error) { this.$error.hidden = false; this.$error.textContent = msg; }
  };

  /* show an error WITHOUT wiping the builder — used for add-to-cart failures so the
     product grid, selections and progress pill stay intact and the shopper can just retry */
  SkioBox.prototype.showCartError = function (msg) {
    if (this.$error) { this.$error.hidden = false; this.$error.textContent = msg; }
  };

  /* reset the builder for a fresh box (used when "Reset box after add to cart" is on) */
  SkioBox.prototype.resetBuilder = function () {
    this.state.selections = {};
    this.state.autoAdded = false;
    this.renderGrid();
    setTimeout(() => {
      this.updateBar();      
    }, 3000);
  };

  /* ---------- add to cart (bottles only) ---------- */
  SkioBox.prototype.addToCart = function () {
    if (this.state.adding) return;
    if (this.total() !== this.state.activeSize) { warn('addToCart blocked: total', this.total(), '!= target', this.state.activeSize); return; }
    step('addToCart');

    const freq = this.state.frequencies[this.state.freqIndex] || null;
    const box = this.state.box;
    const boxIndex = this.state.boxIndex; // this box's unique index -> keeps boxes separate in cart
    const items = [];
    const sel = this.state.selections;
    for (const k in sel) {
      if (!sel.hasOwnProperty(k)) continue;
      const s = sel[k];
      const item = {
        id: Number(numericId(s.variant.platformId)),
        quantity: s.qty,
        properties: { _dynamicBoxId: box.boxId, _dynamicBoxIndex: boxIndex }
      };
      const planId = this.resolveSellingPlan(freq, s.variant.platformId);
      if (planId) item.selling_plan = Number(numericId(planId));
      items.push(item);
    }

    // Welcome Kit: ONE per box (was inside the bottle loop -> one kit per wine).
    // wine-club-cart.js keeps it at qty 1 and removes it when the box leaves the cart.
    const wk = window.welcomeKit;   // set in layout/theme.liquid from Theme settings
    if (wk && wk.variantId) {
      items.push({
        id: Number(wk.variantId),
        quantity: 1,
        properties: { _welcome_kit_product: box.boxId, _gift_product: 'true' }
      });
    }

    //log('frequency:', freq ? freq.label : '(none)', '| boxIndex:', boxIndex);
    log('cart payload:', JSON.parse(JSON.stringify({ items })));

    this.state.adding = true;
    if (this.$error) this.$error.hidden = true; // clear any earlier failure banner for this attempt
    this.updateBar();
    const btn = this.$bar.querySelector('[data-skio-progress]'); // pass to theme pubsub

    fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    }).then((res) => {
      log('cart/add status:', res.status);
      return res.json().then((data) => {
        if (!res.ok) { fail('cart/add rejected:', data); throw new Error(data && data.description ? data.description : `Add to cart failed (${res.status})`); }
        return data;
      });
    }).then((data) => {
      this.state.adding = false;
      // Box is now live in the cart. Keep the SAME boxIndex so later edits target this box.
      this.state.committed = true;
      this._pendingCart = false;
      this._writtenCase = null;
      this.writeCaseAttribute();     // tell the cart which case is being filled
      // Record the freshly-created line keys so later edits UPDATE them instead of re-adding.
      this.mergeAddedLines(data);

      step('added OK');
      log('cart response:', data);

      // Notify theme that the box was added
      document.dispatchEvent(new CustomEvent('skio:box-added', {
        detail: {
          product: data,
          addToCartBtn: btn,
          box,
          items
        }
      }));

      offer_progressbar();

      if (this.redirectToCart) {
        window.location.href = '/cart';
        return;
      }

      // Requirement: do NOT reset the builder after adding — keep it mirroring the box in the cart.
      this.updateBar();

      setTimeout(() => {
        // Open cart drawer
        this.openCartDrawer();        
      }, 250);
    }).catch((err) => {
      fail('add to cart error:', err);
      this.state.adding = false;
      this.updateBar();
      this.showCartError('Something went wrong adding your box to cart. Please try again.');
    });
  };

  /* ---------- live cart sync (only when the box is already committed) ----------
     Existing vs new is decided from this.state.cartLines (built once at hydrate),
     NOT by re-fetching /cart.js on every edit:
       - variant present in cartLines  -> EXISTING line -> POST /cart/change (by key)
       - variant absent + qty > 0       -> NEW item      -> POST /cart/add (with box props)
     Both endpoints hand us enough to keep cartLines fresh, so no extra round-trips. */

  // Refresh cartLines for this box from a full /cart.js-shaped object (from /cart/change).
  SkioBox.prototype.refreshCartLinesFromCart = function (cart) {
    const boxIndex = this.state.boxIndex;
    const map = {};
    (cart && cart.items || []).forEach((it) => {
      if (it.properties && String(it.properties._dynamicBoxIndex) === String(boxIndex)) {
        map[String(it.variant_id)] = { key: it.key, quantity: it.quantity };
      }
    });
    this.state.cartLines = map;
  };

  // Merge added line(s) (from /cart/add response) into cartLines by variant id.
  SkioBox.prototype.mergeAddedLines = function (addResponse) {
    const added = (addResponse && addResponse.items) ? addResponse.items : (addResponse ? [addResponse] : []);
    added.forEach((it) => {
      // box bottles only — the Welcome Kit line must never be treated as a box line
      if (it && it.variant_id != null && it.key && it.properties && it.properties._dynamicBoxIndex != null) {
        this.state.cartLines[String(it.variant_id)] = { key: it.key, quantity: it.quantity };
      }
    });
  };

  SkioBox.prototype._changeLine = function (key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then((r) => r.json()).then((cart) => {
      this.refreshCartLinesFromCart(cart);
      // change.js hands back the WHOLE cart — keep it so nothing downstream
      // has to fetch /cart.js again. (offer_progressbar used to run here too,
      // which meant one extra cart read per line changed.)
      this._lastCart = cart;
      return cart;
    });
  };

  /* Create a new box line. /cart/add is ADDITIVE — if a line for this variant
     already exists (with the same properties) Shopify adds to it instead of
     setting it. We therefore verify the resulting quantity and correct it with an
     absolute /cart/change when it does not match what was asked for. */
  SkioBox.prototype._addLine = function (variantId, quantity, freq) {
    const item = {
      id: Number(variantId),
      quantity: quantity,
      properties: { _dynamicBoxId: this.state.box.boxId, _dynamicBoxIndex: this.state.boxIndex }
    };
    const planId = this.resolveSellingPlan(freq, variantId);
    if (planId) item.selling_plan = Number(numericId(planId));

    return fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] })
    })
      .then((r) => r.json())
      .then((res) => {
        this._lastCart = null;        // add.js returns only the added lines
        this.mergeAddedLines(res);
        const line = this.state.cartLines[String(variantId)];
        if (line && line.key && line.quantity !== quantity) {
          warn('add stacked to', line.quantity, '- correcting to', quantity);
          return this._changeLine(line.key, quantity);
        }
        return res;
      });
  };

  /* Quantity edits, debounced and STRICTLY SERIALISED.
     Why serialised: /cart/change sets an absolute quantity, but /cart/add ADDS to
     whatever is already on the line. If two flushes overlap, the second one still
     sees the variant missing from cartLines (the first flush's response has not
     landed yet), calls /cart/add again, and the quantities stack — e.g. an add of
     4 followed by a target of 9 produced 13 bottles in the cart while the builder
     showed 9. One flush at a time, and re-read cartLines at execution time. */
  SkioBox.prototype.syncLineToCart = function (variant, qty) {
    const variantId = String(Number(numericId(variant.platformId)));

    // A frequency re-map rebuilds every line — hold the edit until it finishes.
    if (this._frequencySyncRunning) {
      this._pendingLineSync = this._pendingLineSync || {};
      this._pendingLineSync[variantId] = { variant: variant, qty: qty };
      return Promise.resolve();
    }

    this._syncQueue = this._syncQueue || {};
    this._syncQueue[variantId] = qty;            // absolute target; last write wins

    clearTimeout(this._syncTimer);
    this._syncTimer = setTimeout(() => this._flushLineSync(), 120);
    return Promise.resolve();
  };

  SkioBox.prototype._flushLineSync = function () {
    // Never run two flushes at once — that is what stacked the quantities.
    if (this._syncing) { this._syncPending = true; return Promise.resolve(); }

    const queue = this._syncQueue || {};
    this._syncQueue = {};
    const ids = Object.keys(queue);
    if (!ids.length) return Promise.resolve();

    this._syncing = true;
    this._hydrating = true;
    this._lastCart = null;
    const freq = this.state.frequencies[this.state.freqIndex] || null;

    const chain = ids.reduce((promise, vId) => promise.then(() => {
      const wantQty = queue[vId];
      // read cartLines HERE, not when the click happened — the previous step in
      // this same chain may have just created the line.
      const existing = this.state.cartLines[vId];

      if (existing && existing.key) return this._changeLine(existing.key, wantQty);
      if (wantQty > 0) return this._addLine(vId, wantQty, freq);
      return Promise.resolve();
    }), Promise.resolve());

    return chain
      .then(() => {
        // Only re-read when the flush ended on an /cart/add.js (which returns just
        // the added lines). A /cart/change.js already gave us the whole cart.
        if (this._lastCart) return this._lastCart;
        return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      })
      .then((cart) => {
        this.refreshCartLinesFromCart(cart);
        try {
          // Hand the cart to everything downstream so none of them re-fetch it:
          //   offer_progressbar -> was a second /cart.js
          //   cart:updated      -> feeds wine-club-cart.js (was a third)
          //   cart:build        -> cart-form re-renders the markup
          offer_progressbar(cart);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          document.dispatchEvent(new CustomEvent('cart:build'));
          if (this._openCartAfterSync) {
            this._openCartAfterSync = false;
            this.openCartDrawer();
          }
        } catch (e) {}
      })
      .catch((err) => { warn('line sync failed:', err); })
      .finally(() => {
        this._syncing = false;
        setTimeout(() => { this._hydrating = false; }, 50);

        // anything queued while we were busy runs now, against fresh cartLines
        if (this._selectionsDirty) { this._selectionsDirty = false; this.syncSelectionsToCart(); return; }
        if (this._syncPending || Object.keys(this._syncQueue || {}).length) {
          this._syncPending = false;
          this._flushLineSync();
        }
      });
  };

  /* Push the WHOLE selection to the cart in one shot.
     Switching case size used to fire one /cart/change.js per bottle — twelve
     sequential round-trips, which is why clearing a case felt frozen. Shopify's
     /cart/update.js takes every line at once, so this is 1 request (plus 1 more
     only if brand-new bottles have to be created). */
  SkioBox.prototype.syncSelectionsToCart = function () {
    if (!this.state.committed) return Promise.resolve();

    // Never overlap with the per-line flush — same reason quantities used to stack.
    if (this._syncing) { this._selectionsDirty = true; return Promise.resolve(); }

    this._syncing = true;
    this._hydrating = true;

    const freq = this.state.frequencies[this.state.freqIndex] || null;

    // what the builder wants the cart to look like
    const desired = {};
    Object.keys(this.state.selections).forEach((k) => {
      const sel = this.state.selections[k];
      desired[String(numericId(sel.variant.platformId))] = sel.qty;
    });

    // every line already in the cart gets its absolute target (0 = remove)
    const updates = {};
    Object.keys(this.state.cartLines).forEach((vId) => {
      updates[this.state.cartLines[vId].key] = desired[vId] || 0;
    });

    // bottles not in the cart yet have to be created
    const adds = Object.keys(desired)
      .filter((vId) => desired[vId] > 0 && !this.state.cartLines[vId])
      .map((vId) => {
        const item = {
          id: Number(vId),
          quantity: desired[vId],
          properties: { _dynamicBoxId: this.state.box.boxId, _dynamicBoxIndex: this.state.boxIndex }
        };
        const planId = this.resolveSellingPlan(freq, vId);
        if (planId) item.selling_plan = Number(numericId(planId));
        return item;
      });

    if (this.isCompleteCase()) this._openCartAfterSync = true;

    const step = Object.keys(updates).length
      ? fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates, attributes: this.caseAttributes() })
        }).then((r) => r.json())
      : Promise.resolve(null);

    return step
      .then((cart) => {
        if (!adds.length) return cart;
        return fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: adds })
        }).then((r) => r.json()).then(() => null);   // add.js does not return the cart
      })
      .then((cart) => (cart && cart.items)
        ? cart
        : fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()))
      .then((cart) => {
        this.refreshCartLinesFromCart(cart);
        try {
          offer_progressbar(cart);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          document.dispatchEvent(new CustomEvent('cart:build'));
          if (this._openCartAfterSync) {
            this._openCartAfterSync = false;
            this.openCartDrawer();
          }
        } catch (e) {}
      })
      .catch((err) => { warn('selection sync failed:', err); })
      .finally(() => {
        this._syncing = false;
        setTimeout(() => { this._hydrating = false; }, 50);
        if (this._selectionsDirty) { this._selectionsDirty = false; this.syncSelectionsToCart(); }
      });
  };

  /* Frequency change while committed: Shopify can't swap selling_plan in place, so
     remove every box line then re-add with the new plan (quantities + props preserved).
     Uses cartLines for the keys — no /cart.js fetch. */
  SkioBox.prototype.syncFrequencyToCart = function () {

    if (this._frequencySyncRunning) {
      return Promise.resolve();
    }

    this._frequencySyncRunning = true;

    const freq = this.state.frequencies[this.state.freqIndex] || null;
    const lines = this.state.cartLines;

    const variantIds = Object.keys(lines);

    if (!variantIds.length) {
      this._frequencySyncRunning = false;
      return Promise.resolve();
    }

    this._hydrating = true;

    const readd = variantIds.map(vId => ({
      id: Number(vId),
      quantity: lines[vId].quantity
    })).filter(x => x.quantity > 0);

    const updates = {};

    variantIds.forEach(vId => {
      updates[lines[vId].key] = 0;
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        updates
      })

    })
    .then(r => {

      if (!r.ok) throw new Error('Remove failed');

      const items = readd.map(x => {
        const item = {
          id: x.id,
          quantity: x.quantity,
          properties: {
            _dynamicBoxId: this.state.box.boxId,
            _dynamicBoxIndex: this.state.boxIndex
          }
        };

        const planId = this.resolveSellingPlan(freq, x.id);
        if (planId) {
          item.selling_plan = Number(numericId(planId));
        }
        return item;

      });

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items
        })
      });

    }).then(r => {
      if (!r.ok) throw new Error('Add failed');
      return r.json();
    }).then(() => {
      return fetch('/cart.js');
    }).then(r => r.json()).then(cart => {

      this.refreshCartLinesFromCart(cart);

      try {
        offer_progressbar();

        document.dispatchEvent(new CustomEvent('skio:box-added', {
          detail: {
            product: cart,
            box: this.state.box,
            items: readd
          }
        }));

        document.dispatchEvent(new CustomEvent('cart:build'));

      } catch(e) {}

      if (this._pendingLineSync) {
        const pending = this._pendingLineSync;
        this._pendingLineSync = null;
        Object.keys(pending).forEach(id => {
          this.syncLineToCart(
            pending[id].variant,
            pending[id].qty
          );
        });
      }

    }).finally(() => {
      this._frequencySyncRunning = false;
      setTimeout(() => {
        this._hydrating = false;
      }, 50);
    }).catch(err => {
      this._frequencySyncRunning = false;
      warn('frequency sync failed:', err);
    });
  };

  /* ---------- Product Details drawer ----------
     "Product Details" fetches an alternate product template and slides it in on
     the right. Two things are rewired after injection so the box keeps working:
       1. the template's own quantity + add-to-cart row is replaced with the
          builder's Add / stepper, so the bottle goes into the BOX (with its
          selling plan and box properties) instead of being bought one-off;
       2. the tab buttons are wired up.
     Each product's markup is fetched once and cached for the session. */

  SkioBox.prototype.detailsUrl = function (handle) {
    return '/products/' + encodeURIComponent(handle) + '?view=product-details';
  };

  SkioBox.prototype.ensureDetailsDrawer = function () {
    if (this.$details) return this.$details;

    const el = document.createElement('div');
    el.className = 'skio-details';
    el.setAttribute('data-skio-details-drawer', '');
    el.hidden = true;
    el.innerHTML =
      '<div class="skio-details__overlay" data-skio-details-close></div>' +
      '<aside class="skio-details__panel" role="dialog" aria-modal="true" aria-label="Product details">' +
        '<button type="button" class="skio-details__close" data-skio-details-close aria-label="Close">' +
          '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M1 1l14 14M15 1L1 15" stroke="currentColor" stroke-width="1.6" fill="none"/></svg>' +
        '</button>' +
        '<div class="skio-details__body" data-skio-details-body></div>' +
      '</aside>';
    document.body.appendChild(el);
    this.$details = el;
    this.$detailsBody = el.querySelector('[data-skio-details-body]');

    el.addEventListener('click', (e) => {
      if (e.target.closest('[data-skio-details-close]')) { this.closeDetails(); return; }

      if (!this._detailsProduct) return;
      if (e.target.closest('[data-skio-details-inc]')) { this.setDetailsQty(+1); return; }
      if (e.target.closest('[data-skio-details-dec]')) { this.setDetailsQty(-1); return; }
      if (e.target.closest('[data-skio-details-add]')) { this.addToBoxFromDetails(); return; }

      const tab = e.target.closest('.product-tabs button');
      if (tab) this.activateDetailsTab(tab);
    });

    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' && this.$details && !this.$details.hidden) this.closeDetails();
    });

    // The cart drawer opening (e.g. the box just completed) takes precedence.
    document.addEventListener('CartDrawer:open', () => this.closeDetails());

    return el;
  };

  /* Panes are matched by data-tab / data-pane, so a commented-out pane can never
     shift the wrong panel into view (the old index pairing broke on exactly that). */
  SkioBox.prototype.activateDetailsTab = function (btn) {
    const tabs = btn.parentNode;
    const wrap = tabs.parentNode.querySelector('.tab-content');
    if (!wrap) return;

    const name = btn.dataset.tab;
    const buttons = Array.prototype.slice.call(tabs.querySelectorAll('button'));
    const panes = Array.prototype.slice.call(wrap.querySelectorAll('.tab-pane'));

    if (name) {
      const pane = wrap.querySelector('[data-pane="' + cssEscape(name) + '"]');
      if (!pane) return;                                  // no matching pane -> ignore
      buttons.forEach((b) => b.classList.toggle('active', b === btn));
      panes.forEach((p) => p.classList.toggle('active', p === pane));
      return;
    }

    const index = buttons.indexOf(btn);                   // fallback for untagged markup
    buttons.forEach((b, i) => b.classList.toggle('active', i === index));
    panes.forEach((p, i) => p.classList.toggle('active', i === index));
  };

  /* Swap the template's cart row for the builder's own control, then fill the
     dynamic slots (offer threshold + shipping note) from the live box state. */
  SkioBox.prototype.injectDetailsAction = function (product) {
    if (!this.$detailsBody) return;
    const scope = this.$detailsBody;

    /* The fetched template ships the theme's real add-to-cart (block-buy-buttons +
       block-quantity-selector). If any of it survives, clicking it buys the bottle
       as a ONE-TIME product instead of putting it in the box — which is exactly
       what happened: bottles landed under "One Time Purchase Only" with no box
       properties and no selling plan.
       So: strip every theme purchase control out of the injected markup first,
       then drop our own Add-to-Box control in. Nothing here depends on the
       template keeping a specific wrapper class. */
    const purge = [
      'product-form', '.product-form',
      '.wine-club-atc-btn', '.quantity-box',
      '[data-buy-buttons]', '.product__submit', '.shopify-payment-button'
    ];
    purge.forEach((sel) => {
      scope.querySelectorAll(sel).forEach((el) => el.remove());
    });

    let row = scope.querySelector('.cart-row');
    if (!row) {
      // template has no .cart-row (or it was purged) -> create one in the right place
      row = document.createElement('div');
      row.className = 'cart-row';
      const before = scope.querySelector('.shipping-note') ||
                     scope.querySelector('.product-tabs') ||
                     null;
      const host = scope.querySelector('.custom-product-wrapper') || scope;
      if (before && before.parentNode) before.parentNode.insertBefore(row, before);
      else host.appendChild(row);
    }

    // Show what is already in the box for this bottle, not a blank 1. Opening the
    // drawer for a bottle you put 4 of in the box now reads 4, and the stepper
    // edits that number directly.
    this._detailsQty = this.detailsCurrentQty() || 1;
    row.innerHTML = this.detailsActionHtml();

    this.updateDetailsOffer();
    this.refreshReviews();                      // let Loox draw the rating in the drawer
  };

  SkioBox.prototype.updateDetailsOffer = function () {
    if (!this.$detailsBody) return;

    const total = this.total();
    const target = this.state.activeSize || 0;
    const remaining = Math.max(0, target - total);
    const pct = this.activeTierPercent();

    const offer = this.$detailsBody.querySelector('[data-skio-offer]');
    if (offer) {
      offer.hidden = false;
      if (target > 0 && total >= target) {
        offer.classList.add('is-complete');
        offer.innerHTML = '<b>Minimum purchase reached!</b>' + '<span>Unlocked Wine Club Membership!</span>';
      } else {
        offer.classList.remove('is-complete');
        offer.innerHTML = '<span>Add ' + remaining + ' more ' + (remaining === 1 ? 'bottle' : 'bottles') + ' to unlock the Wine Club </span>';
      }
    }

    const ship = this.$detailsBody.querySelector('[data-skio-shipnote]');
    if (ship) {
      ship.textContent = (target > 0 && total >= target)
        ? 'Free shipping included with your Wine Club case'
        : 'Free shipping on every Wine Club case';
    }
  };

  /* Quantity chooser + a single ADD TO BOX button. Deliberately NOT the grid's
     stepper: here the shopper picks an amount first, then commits it in one go. */
  /* How many of THIS bottle are in the box right now. */
  SkioBox.prototype.detailsCurrentQty = function () {
    const product = this._detailsProduct;
    if (!product) return 0;
    return this.qtyOf(this.activeVariant(product).platformId);
  };

  /* The biggest number the drawer may show: what is in the box for this bottle,
     plus whatever room is left in the case. */
  SkioBox.prototype.detailsMaxQty = function () {
    return this.detailsCurrentQty() + this.remainingToCase();
  };

  SkioBox.prototype.detailsActionHtml = function () {
    const product = this._detailsProduct;
    if (!product) return '';
    const variant = this.activeVariant(product);

    if (!this.isAvailable(variant)) {
      return '<span class="skio-btn skio-btn--soldout" aria-disabled="true">Soldout</span>';
    }

    const current = this.detailsCurrentQty();
    const qty = this._detailsQty || 1;
    // Nothing to do when the number on screen already matches the box and the
    // case has no room left.
    const stuck = this.remainingToCase() <= 0 && qty === current;
    const label = stuck ? 'Case is full' : (current > 0 ? 'Update Box' : 'Add to Box');

    return '<div class="skio-details__qty">' +
        '<button type="button" class="skio-details__qty-btn" data-skio-details-dec aria-label="Remove one">&minus;</button>' +
        '<span class="skio-details__qty-num" data-skio-details-qty>' + qty + '</span>' +
        '<button type="button" class="skio-details__qty-btn" data-skio-details-inc aria-label="Add one">+</button>' +
      '</div>' +
      '<button type="button" class="skio-btn skio-details__atc" data-skio-details-add' +
        (stuck ? ' disabled' : '') + '>' + label + '</button>';
  };

  /* Absolute editor: the number shown IS the quantity this bottle will have in
     the box, so it can go down to 0 (remove) and up to the case ceiling. */
  SkioBox.prototype.setDetailsQty = function (delta) {
    const max = this.detailsMaxQty();
    let next = (this._detailsQty || 0) + delta;
    if (next < 0) next = 0;
    if (next > max) { next = max; this.flashDetails(); }
    this._detailsQty = next;

    const el = this.$detailsBody && this.$detailsBody.querySelector('[data-skio-details-qty]');
    if (el) el.textContent = next;

    const btn = this.$detailsBody && this.$detailsBody.querySelector('[data-skio-details-add]');
    if (btn) {
      const current = this.detailsCurrentQty();
      const stuck = this.remainingToCase() <= 0 && next === current;
      btn.disabled = stuck;
      btn.textContent = stuck ? 'Case is full' : (next === 0 ? 'Remove from Box' : (current > 0 ? 'Update Box' : 'Add to Box'));
    }
  };

  /* Commit the chosen amount to the box in one action. */
  SkioBox.prototype.addToBoxFromDetails = function () {
    const product = this._detailsProduct;
    if (!product) return;

    const variant = this.activeVariant(product);
    if (!this.isAvailable(variant)) return;

    const key = String(variant.platformId);
    const prevSel = this.state.selections[key];
    const current = prevSel ? prevSel.qty : 0;

    // The stepper holds the TARGET quantity for this bottle, so set it outright.
    const next = Math.min(this._detailsQty || 0, this.detailsMaxQty());
    if (next === current) { this.flashDetails(); return; }

    if (next === 0) delete this.state.selections[key];
    else this.state.selections[key] = {
      product: product, variant: variant, qty: next,
      seq: (prevSel && prevSel.seq) || ++this._seq
    };
    const add = next - current;
    log('detailsSetQty:', product.productTitle, current, '->', next, '| total', this.total(), '/', this.state.activeSize);

    // Held locally until the case is complete — same rule as the grid.
    if (this.state.committed) this._pendingCart = true;

    this.updateCard(product.productPlatformId);   // grid card reflects the new count
    this.updateBar();                             // progress + first-time auto add to cart

    // keep showing the box's new value for this bottle
    this._detailsQty = next;
    const row = this.$detailsBody && this.$detailsBody.querySelector('.cart-row');
    if (row) row.innerHTML = this.detailsActionHtml();
    this.updateDetailsOffer();
    this.flashDetailsAdded(add);
  };

  SkioBox.prototype.flashDetails = function () {
    const row = this.$detailsBody && this.$detailsBody.querySelector('.cart-row');
    if (!row) return;
    row.classList.add('skio-shake');
    setTimeout(() => row.classList.remove('skio-shake'), 450);
  };

  SkioBox.prototype.flashDetailsAdded = function (n) {
    const btn = this.$detailsBody && this.$detailsBody.querySelector('[data-skio-details-add]');
    if (!btn || btn.disabled) return;
    const original = btn.textContent;
    btn.textContent = n > 0 ? 'Added ' + n + ' to box' : 'Removed ' + Math.abs(n);
    btn.classList.add('is-added');
    clearTimeout(this._addedTimer);
    this._addedTimer = setTimeout(() => {
      btn.classList.remove('is-added');
      if (this.$detailsBody && this.$detailsBody.contains(btn)) btn.textContent = original;
    }, 1400);
  };

  SkioBox.prototype.syncDetailsAction = function (productId) {
    if (!this.$details || this.$details.hidden) return;
    if (!this._detailsProduct) return;
    // capacity may have changed -> refresh the chooser (keeps "Box is full" honest)
    const row = this.$detailsBody.querySelector('.cart-row');
    if (row) {
      this._detailsQty = this.detailsCurrentQty();   // mirror the box
      row.innerHTML = this.detailsActionHtml();
    }
    this.updateDetailsOffer();
  };

  SkioBox.prototype.openDetails = function (handle) {
    const product = this.products().filter((p) => String(p.productHandle) === String(handle))[0];
    if (!product) { warn('no box product for handle', handle); return; }

    this._detailsProduct = product;
    this._detailsCache = this._detailsCache || {};

    const el = this.ensureDetailsDrawer();
    el.hidden = false;
    document.body.classList.add('skio-details-open');
    // next frame so the transition runs
    requestAnimationFrame(() => el.classList.add('is-open'));

    const cached = this._detailsCache[handle];
    if (cached) {
      this.$detailsBody.innerHTML = cached;
      this.injectDetailsAction(product);
      return;
    }

    this.$detailsBody.innerHTML = '<div class="skio-details__loading">Loading…</div>';

    fetch(this.detailsUrl(handle), { headers: { Accept: 'text/html' } })
      .then((res) => {
        if (!res.ok) throw new Error('details ' + res.status);
        return res.text();
      })
      .then((html) => {
        this._detailsCache[handle] = html;
        if (this._detailsProduct !== product) return;   // shopper opened another one meanwhile
        this.$detailsBody.innerHTML = html;
        this.injectDetailsAction(product);
      })
      .catch((err) => {
        warn('product details failed:', err);
        this.$detailsBody.innerHTML =
          '<div class="skio-details__loading">Details are unavailable right now. ' +
          '<a href="/products/' + esc(handle) + '">View the product page</a>.</div>';
      });
  };

  SkioBox.prototype.closeDetails = function () {
    if (!this.$details) return;
    this.$details.classList.remove('is-open');
    document.body.classList.remove('skio-details-open');
    this._detailsProduct = null;
    setTimeout(() => { if (this.$details && !this.$details.classList.contains('is-open')) this.$details.hidden = true; }, 300);
  };

  /* ---------- events ---------- */
  // SkioBox.prototype.bindEvents = function () {
  //   this.root.addEventListener('click', (e) => {
  //     const tier = e.target.closest('[data-skio-tier]');
  //     if (tier) { this.setTier(Number(tier.dataset.skioTier)); return; }
  //     const details = e.target.closest('[data-skio-details]');
  //     if (details) { this.openDetails(details.dataset.handle); return; }
  //     const card = e.target.closest('[data-skio-card]');
  //     if (card) {
  //       const product = this.products().filter((p) => String(p.productPlatformId) === String(card.dataset.productId))[0];
  //       if (!product) return;
  //       if (e.target.closest('[data-skio-add]') || e.target.closest('[data-skio-inc]')) { this.changeQty(product, +1); return; }
  //       if (e.target.closest('[data-skio-dec]')) { this.changeQty(product, -1); return; }
  //     }
  //   });
  //   this.root.addEventListener('change', (e) => {
  //     const vSel = e.target.closest('[data-skio-variant]');
  //     if (vSel) { this.setCardVariant(vSel.dataset.productId, vSel.value); return; }
  //     const tSel = e.target.closest('[data-skio-tier-select]');
  //     if (tSel) { this.setTier(Number(tSel.value)); return; }
  //     const fSel = e.target.closest('[data-skio-freq-select]');
  //     if (fSel) { this.setFreq(Number(fSel.value)); return; }
  //   });
  // };

  SkioBox.prototype.bindEvents = function () {
    this.root.addEventListener('click', (e) => {
      const info = e.target.closest('[data-skio-info]');
      if (info) {
        const tip = this.$bar.querySelector('[data-skio-tooltip]');
        if (tip) tip.hidden = !tip.hidden;
        return;
      }
      const tier = e.target.closest('[data-skio-tier]');
      if (tier) { this.setTier(Number(tier.dataset.skioTier)); return; }
      const details = e.target.closest('[data-skio-details]');
      if (details) { this.openDetails(details.dataset.handle); return; }
      const card = e.target.closest('[data-skio-card]');
      if (card) {
        const product = this.products().filter((p) => String(p.productPlatformId) === String(card.dataset.productId))[0];
        if (!product) return;
        if (e.target.closest('[data-skio-add]') || e.target.closest('[data-skio-inc]')) { this.changeQty(product, +1); return; }
        if (e.target.closest('[data-skio-dec]')) { this.changeQty(product, -1); return; }
      }
    });
    this.root.addEventListener('change', (e) => {
      const vSel = e.target.closest('[data-skio-variant]');
      if (vSel) { this.setCardVariant(vSel.dataset.productId, vSel.value); return; }
      const tSel = e.target.closest('[data-skio-tier-select]');
      if (tSel) { this.setTier(Number(tSel.value)); return; }
      const fSel = e.target.closest('[data-skio-freq-select]');
      if (fSel) { this.setFreq(Number(fSel.value)); return; }
    });
    // document.addEventListener('hover', (e) => {
    //   const tip = this.$bar && this.$bar.querySelector('[data-skio-tooltip]');
    //   if (!tip || tip.hidden) return;
    //   if (e.target.closest('[data-skio-info]') || e.target.closest('[data-skio-tooltip]')) return;
    //   tip.hidden = true;
    // });
  };

  /* ---------- boot ---------- */
  ready(function () {
    const roots = document.querySelectorAll('[data-skio-bab]');
    if (!roots.length) { warn('No [data-skio-bab] section found.'); return; }
    roots.forEach((root) => new SkioBox(root).init());
  });

  // Exposed so the cart controller reuses THIS (bottle-aware) version.
  window.offer_progressbar = offer_progressbar;

  function offer_progressbar(cartData) {
    // Reuse the cart we were handed; only hit the network when we have none.
    const source = (cartData && cartData.items)
      ? Promise.resolve(cartData)
      : fetch("/cart.js").then((response) => response.json());

    source
      .then((cart) => {
        var cartItems = cart.items;
        const hasBundleProduct = cartItems.some(item => item.properties.hasOwnProperty("_bundleBoxSize"));
        var singleBottles = cartItems.filter((item) => item.properties["_singlebottle"]);
        var singleBottleCount = singleBottles.reduce((total, item) => {
          return total + (item.quantity || 0);
        }, 0);
        // Wine Club box lines carry _dynamicBoxIndex, not _singlebottle / _bottlecount.
        // Without this they counted as zero bottles AND as "normal products", so the
        // drawer kept showing "add 12 more bottles" on a completed 12-bottle case.
        var boxBottleItems = cartItems.filter((item) => item.properties && item.properties["_dynamicBoxIndex"] != null);
        var boxBottleCount = boxBottleItems.reduce((total, item) => total + (item.quantity || 0), 0);

        var multiBottlesProducts = cartItems.filter((item) => item.properties["_bottlecount"]);    
        var multiBottlesCount = multiBottlesProducts.reduce((total, item) => {
          return (total + (parseInt(item.properties["_bottlecount"], 10)) * item.quantity || 0);
        }, 0);
        var totleBottles = singleBottleCount + multiBottlesCount + boxBottleCount;
        var freeShipProduct = cartItems.filter((item) => item.properties["_free_ship"]);     
        var noFreeShipProduct = cartItems.filter((item) => !item.properties["_free_ship"]);     
        var normalProduct =  cartItems.filter((item) => !(item.properties && (item.properties["_singlebottle"] || item.properties["_bottlecount"] || item.properties["_free_ship"] || item.properties["_dynamicBoxIndex"] != null || item.properties["_welcome_kit_product"] != null)));

        $(".offer-progressbar_txtop").parent().removeClass('free-ship-active');

        if (theme.free_product_enable) {
          function getCookie(name) {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(';').shift();
          }

          const eventCookie = getCookie('discount_code');
          const discountCodes = theme.discount_code;
          const discountProductIds = theme.discount_productIds;
          const discountCollections = theme.discount_collection;
          const thresold_quantity_text = theme.thresold_quantity_text; // Array of thresholds as strings

          if (eventCookie && discountCodes.includes(eventCookie)) {
            const codeIndex = discountCodes.indexOf(eventCookie);
            const freeProductId = discountProductIds[codeIndex];
            const collectionProducts = discountCollections[eventCookie]; // Array of products
            const eligibleProductIds = collectionProducts.map(product => product.id);
            const hasFreeProduct = cartItems.some(item => item.id === freeProductId);

            const thresholdValue = thresold_quantity_text[codeIndex]; // e.g. "12" or "$130"

            // Calculate total quantity or total price of eligible products in cart
            let totalQuantity = 0;
            let totalPrice = 0;

            cartItems.forEach(item => {
              if (eligibleProductIds.includes(item.product_id)) {
                totalQuantity += item.quantity;
                totalPrice += item.quantity * (item.price / 100); // assuming item.price is unit price
              }
            });

            
            // Check threshold type
            let isEligible = false;
            if (thresholdValue.startsWith('$')) {
              // Price based threshold
              const priceThreshold = parseFloat(thresholdValue.replace('$', ''));
              if (totalPrice >= priceThreshold) {
                isEligible = true;
              }
            } else {
              // Quantity based threshold
              const quantityThreshold = parseInt(thresholdValue, 10);
              if (totalQuantity >= quantityThreshold) {
                isEligible = true;
              }
            }

            if (isEligible && !hasFreeProduct) {
              // `this` is not the component inside offer_progressbar, so
              // this.addFreeProd() threw a TypeError and killed the whole
              // progress-bar update. Use the function only when it exists here.
              if (typeof addFreeProd === 'function') {
                addFreeProd(freeProductId);
              } else if (typeof window.addFreeProd === 'function') {
                window.addFreeProd(freeProductId);
              }
            }
          }
        }
        
        //1. if only freeship product in cart
        if(freeShipProduct.length > 0 && normalProduct.length == 0 && noFreeShipProduct.length == 0){
            if($('body').hasClass('template-cart')){
            $(".offer-progressbar_txtop").empty().html(cart_free_shipping_note);
            }else{
            $(".offer-progressbar_txtop").empty().html(free_shipping_note);
            $(".offer-progressbar_txtop").parent().addClass('free-ship-active');
            } 
        } 
          
        //2. if freeship product and normal product in cart
        else if(freeShipProduct.length > 0 && normalProduct.length > 0 && discount_thresold_1 > cart.total_price){
          if($('body').hasClass('template-cart')){
          $(".offer-progressbar_txtop").empty().html(cart_free_shipping_note);
          }else{
          $(".offer-progressbar_txtop").empty().html(free_shipping_note);
            $(".offer-progressbar_txtop").parent().addClass('free-ship-active');
          }
        }
          
        //3. if freeship product and bottle product in cart  
        else if(freeShipProduct.length > 0 && normalProduct.length == 0 && totleBottles < 12){
          if($('body').hasClass('template-cart')){
            $(".offer-progressbar_txtop").empty().html(cart_free_shipping_note);
            }else{
            $(".offer-progressbar_txtop").empty().html(free_shipping_note);
            $(".offer-progressbar_txtop").parent().addClass('free-ship-active');
            }
        }
          
        //4. if only bottle product in cart
        else if(normalProduct.length == 0 && totleBottles > 0 && totleBottles < 12){
          const remainingBottles = 12 - totleBottles;          
          if($('body').hasClass('template-cart')){
            if(cart_remaining_note){
              $(".offer-progressbar_txtop").empty().html(cart_remaining_note.replaceAll("X", remainingBottles));
            }
            
          }else{
              $(".offer-progressbar_txtop").parent().removeClass('free-ship-active');
              $(".offer-progressbar_txtop").empty().html(remaining_note.replaceAll("X", remainingBottles));
          }
        }
          
        //5. if only normal product in cart  
        else if(normalProduct.length > 0 && discount_thresold_1 > cart.total_price){
          if(!$('body').hasClass('template-cart')){
            $(".offer-progressbar_txtop").parent().removeClass('free-ship-active');
          }
          const remainingBottles = 12 - totleBottles;
          const remainingPrice = discount_thresold_1 - cart.total_price;
          const remainigMsg = free_product.replaceAll('X',remainingBottles).replaceAll('$0.00',theme.Currency.formatMoney(remainingPrice))
          $(".offer-progressbar_txtop").empty().html(remainigMsg);
        }
          
        //6. if there is no normal product and bottle product more then 12  
        else if(totleBottles >= 12 && normalProduct.length == 0){
          if($('body').hasClass('template-cart')){
            $(".offer-progressbar_txtop").empty().html(cart_free_shipping_note);
            }else{
            $(".offer-progressbar_txtop").empty().html(free_shipping_note);
            $(".offer-progressbar_txtop").parent().addClass('free-ship-active');
            } 
        }else{
            if($('body').hasClass('template-cart')){
            $(".offer-progressbar_txtop").empty().html(cart_free_shipping_note);
            }else{
            $(".offer-progressbar_txtop").empty().html(free_shipping_note);
              $(".offer-progressbar_txtop").parent().addClass('free-ship-active');
            }            
        }  
        
        if (hasBundleProduct) {
            let remainingNote = free_shipping_note;
            $(".offer-progressbar_txtop").parent().addClass('free-ship-active');
            $(".offer-progressbar_txtop").empty().html(remainingNote);
        } 
  
      })
      .catch(error => {
        console.error("Error fetching cart data: ", error);
      });
  }
})();